import axios from 'axios'
import { Toast } from 'vant'
import router from '@/router'

const instance = axios.create({
  timeout: 60000, // 超时时间 60s
  baseURL: process.env.NODE_ENV === 'production' ? 'https://ltbu.zjshtc.com' : ''
})

instance.defaults.headers.post['Content-Type'] = 'application/json';

// 不需要展示全局 loading 的接口（如状态轮询）
const SILENT_URLS = []

// 从本地登录信息中读取 uid/token（localStorage 脏数据时兜底，避免拦截器崩溃）
const getAuth = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData'))
    return userData ? { uid: userData.uid, token: userData.token } : {}
  } catch (e) {
    return {}
  }
}

/** 添加请求拦截器 **/
instance.interceptors.request.use(config => {
  // 统一注入登录态：GET 放 params，其余放 body（FormData 走 token header）
  const { uid, token } = getAuth()
  if (config.data instanceof FormData) {
    if (token) {
      config.headers.token = token
    }
  } else if (config.method === 'get') {
    config.params = { uid, token, ...config.params }
  } else {
    config.data = { uid, token, ...config.data }
  }
  if (!config.silent && !SILENT_URLS.some(url => config.url.indexOf(url) > -1)) {
    Toast.loading({
      duration: 0,
      forbidClick: true,
      message: '加载中'
    })
  }
  return config
}, error => {
  return Promise.reject(error)
})

const getErrMsg = (data) => data.message || data.resultDesc || data.msg

const clearLogin = () => {
  localStorage.removeItem('userLogin')
  localStorage.removeItem('userData')
}

// 清登录态后跳回登录页（已在登录页时不重复跳转，避免 NavigationDuplicated）
// 携带当前路由 query：被踢回登录页时保留邀请参数（refereeNoId 等）和回跳地址
const redirectLogin = () => {
  if (router.currentRoute.name !== 'LoginView') {
    router.push({
      name: 'LoginView',
      query: { ...router.currentRoute.query, redirect: router.currentRoute.fullPath }
    })
  }
}

/** 添加响应拦截器  **/
instance.interceptors.response.use(response => {
  // raw 模式：业务码由调用方自行处理（如 OCR 识别失败码 21/22）
  if (response.config.raw) {
    Toast.clear()
    return Promise.resolve(response.data)
  }
  const data = response.data
  if (data.code == 0 || data.resultCode == 0) {
    Toast.clear()
    return Promise.resolve(data)
  }
  const msg = getErrMsg(data)
  Toast.fail(msg)
  if (data.code == 100 || data.resultCode == 100 || [102, 131].includes(data.code) || [102, 131].includes(data.resultCode)) {
    // 登录态失效（未登录/token 过期），清登录态并跳回登录页
    clearLogin()
    redirectLogin()
  }
  return Promise.reject(msg)
}, error => {
  Toast.clear()
  const isTimeout = error.code === 'ECONNABORTED' || (error.message || '').includes('timeout')
  // 带上 HTTP 状态码，用户截图反馈时能直接定位是后端 500 还是真断网
  const status = error.response && error.response.status
  const msg = isTimeout
    ? '请求超时，请重试'
    : status
      ? `网络异常(${status})，请稍后重试`
      : '网络异常，请检查网络后重试'
  console.log('request fail', error.config && error.config.url, status, error.message)
  // silent 请求（探测/轮询类）的错误不全局提示，由调用方自行兜底
  if (!(error.config && error.config.silent)) {
    Toast.fail(msg)
  }
  return Promise.reject(new Error(msg))
})

/* 统一封装get请求 */
export const get = (url, params = {}, config = {}) => {
  return instance({ method: 'get', url, params, ...config })
}

/* 统一封装post请求 */
export const post = (url, data = {}, config = {}) => {
  return instance({ method: 'post', url, data, ...config })
}

/* 统一封装put请求 */
export const put = (url, data = {}, config = {}) => {
  return instance({ method: 'put', url, data, ...config })
}

/* 统一封装delete请求 */
export const deleted = (url, data = {}, config = {}) => {
  return instance({ method: 'delete', url, data, ...config })
}

/* 统一封装文件上传（multipart/form-data） */
export const upload = (url, formData, config = {}) => {
  return instance({
    method: 'post',
    url,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
    ...config
  })
}

/**
 * 附件下载（GET）。uid/token 由请求拦截器注入 query。
 * raw 模式绕过业务码拦截器；失败时后端可能在 HTTP 200 返回 JSON，
 * 需检查 blob.type：是 JSON 则解析出错误信息抛出，否则返回 Blob。
 */
export const downloadFile = async (url, params = {}) => {
  Toast.loading({ duration: 0, forbidClick: true, message: '文件加载中' })
  try {
    const blob = await instance.get(url, { params, responseType: 'blob', raw: true, silent: true })
    if ((blob.type || '').includes('json')) {
      const text = await blob.text()
      const result = JSON.parse(text)
      throw new Error(result.resultView || result.resultDesc || '下载失败')
    }
    return blob
  } finally {
    Toast.clear()
  }
}
